import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stack,
  Button,
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  useMediaQuery,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useTheme, alpha, darken } from "@mui/material/styles";
import { QRCodeSVG } from "qrcode.react";
import { Tournament } from "../types";
import { apiRequest } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import { useOrganization } from "../contexts/OrganizationContext";
import { getBracketNames } from "../utils/bracketNames";
import { withByeCards } from "../utils/scheduleUtils";

const BRACKET_ROUND_ORDER = [
  "Round 1", "Round 2", "Round 3", "Round 4", "Round 5",
  "round-of-32", "round-of-16", "quarter-finals", "semi-finals", "final",
];
const CONSOLATION_ORDER = ["consolation-semi-finals", "seventh-place", "fifth-place", "third-place"];
const isConsolationRound = (round?: string) => !!round && CONSOLATION_ORDER.includes(round);

function sortRounds(rounds: string[], consolation: boolean) {
  const order = consolation ? CONSOLATION_ORDER : BRACKET_ROUND_ORDER;
  return [...rounds].sort((a, b) => {
    const ai = order.indexOf(a), bi = order.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
}

const formatRoundLabel = (round?: string) =>
  round ? round.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";

const formatTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : null;

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : null;

interface FlatMatch {
  id: string;
  stage: "group" | "knockout";
  groupLabel?: string;
  bracketLabel?: string;
  round?: string;
  team1Id?: string;
  team2Id?: string;
  team1Placeholder?: string;
  team2Placeholder?: string;
  previousMatch1?: number;
  previousMatch2?: number;
  matchNumber?: number;
  team1Games?: number | null;
  team2Games?: number | null;
  winnerId?: string;
  startTime?: string | null;
  courtNumber?: number | null;
  isScored: boolean;
  isCancelled?: boolean;
  isWalkover?: boolean;
  isByeCard?: boolean;
  byeTeamId?: string;
}

type MyGiftResponse = {
  giftsEnabled: boolean;
  giftDescription?: string;
  giftOptions?: string[];
  redeemed: boolean;
  redeemedAt?: any;
  qrPayload?: string;
};

const formatGiftDateTime = (value: any): string => {
  if (!value) return "";
  let date: Date | null = null;

  if (value instanceof Date) {
    date = value;
  } else if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else if (typeof value?.seconds === "number") {
    date = new Date(value.seconds * 1000);
  } else if (typeof value?._seconds === "number") {
    date = new Date(value._seconds * 1000);
  } else {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      date = parsed;
    }
  }

  if (!date) return "";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function TabPanel({ value, index, children }: { value: number; index: number; children: React.ReactNode }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

export default function TournamentInfo() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { playerUser } = useAuth();
  const { currentOrg } = useOrganization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const bracketNames = getBracketNames(currentOrg?.slug);

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [myGift, setMyGift] = useState<MyGiftResponse | null>(null);
  const [myGiftLoading, setMyGiftLoading] = useState(false);

  useEffect(() => {
    fetchTournament();
    const interval = setInterval(fetchTournament, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchTournament = async () => {
    try {
      const res = await apiRequest(`/api/tournaments/${id}`);
      if (res.ok) setTournament(await res.json());
    } catch (e) {
      console.error("Failed to fetch tournament:", e);
    } finally {
      setLoading(false);
    }
  };

  const getTeamName = (teamId?: string) => {
    if (!teamId || !tournament) return "TBD";
    const team = tournament.registeredTeams?.find((r) => r.id === teamId);
    if (!team) return "TBD";
    if (team.player1Name && team.player2Name) return `${team.player1Name} / ${team.player2Name}`;
    return team.teamName || "TBD";
  };

  const getMatchLabel = (matchNumber?: number) =>
    matchNumber === undefined || matchNumber === null ? "" : `#${matchNumber + 1}`;

  const getKnockoutSideLabel = (m: FlatMatch, isTeam1: boolean) => {
    const teamId = isTeam1 ? m.team1Id : m.team2Id;
    if (teamId) return getTeamName(teamId);
    const prevMatch = isTeam1 ? m.previousMatch1 : m.previousMatch2;
    if (prevMatch !== undefined && prevMatch !== null) {
      const isLoserMatch = isConsolationRound(m.round) || m.round === "third-place";
      return `${isLoserMatch ? "Loser" : "Winner"} of Match ${getMatchLabel(prevMatch)}`;
    }
    const placeholder = isTeam1 ? m.team1Placeholder : m.team2Placeholder;
    return placeholder || "TBD";
  };

  const getAllMatches = (): FlatMatch[] => {
    if (!tournament) return [];
    const matches: FlatMatch[] = [];

    tournament.groups?.forEach((group) => {
      group.matchResults?.forEach((m, idx) => {
        const gamesPlayed = (m.team1Games || 0) + (m.team2Games || 0);
        matches.push({
          id: `group-${group.id}-${idx}`,
          stage: "group",
          groupLabel: group.name,
          team1Id: m.team1Id,
          team2Id: m.team2Id,
          team1Games: m.team1Games,
          team2Games: m.team2Games,
          startTime: m.startTime || null,
          courtNumber: m.courtNumber || null,
          isScored: gamesPlayed > 0,
        });
      });
    });

    const mc = withByeCards(tournament.knockoutBracket?.mastersCup || (tournament as any).mastersCupBracket || []);
    const tc = withByeCards(tournament.knockoutBracket?.tourCup || (tournament as any).tourCupBracket || []);

    [
      { list: mc, label: bracketNames.primary },
      { list: tc, label: bracketNames.secondary },
    ].forEach(({ list, label }) => {
      (list || []).forEach((m: any) => {
        if (m.isPlaceholder) return;
        if (m.isByeCard) {
          matches.push({
            id: m.id,
            stage: "knockout",
            bracketLabel: label,
            round: m.round,
            isScored: false,
            isByeCard: true,
            byeTeamId: m.byeTeamId,
          });
          return;
        }
        matches.push({
          id: m.id || m.matchId,
          stage: "knockout",
          bracketLabel: label,
          round: m.round,
          team1Id: m.team1Id,
          team2Id: m.team2Id,
          team1Placeholder: m.team1Placeholder,
          team2Placeholder: m.team2Placeholder,
          previousMatch1: m.previousMatch1,
          previousMatch2: m.previousMatch2,
          matchNumber: m.matchNumber,
          team1Games: m.team1Games,
          team2Games: m.team2Games,
          winnerId: m.winnerId,
          startTime: m.startTime || null,
          courtNumber: m.courtNumber || null,
          isScored: !!m.winnerId,
          isCancelled: !!m.isCancelled,
          isWalkover: !!m.isWalkover,
        });
      });
    });

    return matches;
  };

  const myTeam = tournament?.registeredTeams?.find(
    (t) => t.player1Id === playerUser?.playerId || t.player2Id === playerUser?.playerId,
  );

  useEffect(() => {
    const fetchMyGift = async () => {
      if (!tournament?.id || !playerUser || !myTeam) {
        setMyGift(null);
        return;
      }

      setMyGiftLoading(true);
      try {
        const response = await apiRequest(`/api/tournaments/${tournament.id}/my-gift`);
        const data = await response.json();

        if (!response.ok) {
          if (response.status === 403 || response.status === 404) {
            setMyGift(null);
            return;
          }
          throw new Error(data.error || "Failed to load gift QR");
        }

        if (data?.giftsEnabled) {
          setMyGift(data);
        } else {
          setMyGift(null);
        }
      } catch (giftError) {
        console.error("Failed to fetch gift QR info:", giftError);
        setMyGift(null);
      } finally {
        setMyGiftLoading(false);
      }
    };

    fetchMyGift();
  }, [myTeam, playerUser, tournament?.id]);

  const stageLabel = (m: FlatMatch) =>
    m.stage === "group" ? m.groupLabel || "Group" : `${m.bracketLabel} · ${formatRoundLabel(m.round)}`;

  // ---- Standings tab ----
  const renderStandingsTab = () => {
    if (!tournament?.groups || tournament.groups.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">Groups haven't been generated yet. Check back closer to the tournament date.</Typography>
        </Box>
      );
    }
    return (
      <Grid container spacing={2}>
        {tournament.groups.map((group) => {
          const standings = group.standings && group.standings.length > 0
            ? [...group.standings].sort((a, b) =>
                b.points !== a.points ? b.points - a.points : b.gamesDiff !== a.gamesDiff ? b.gamesDiff - a.gamesDiff : b.gamesWon - a.gamesWon)
            : group.teamIds.map((teamId) => ({ teamId, wins: 0, draws: 0, losses: 0, points: 0, gamesWon: 0, gamesLost: 0, gamesDiff: 0, matchesPlayed: 0 }));
          return (
            <Grid item xs={12} md={6} key={group.id}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" fontWeight={700} gutterBottom>{group.name}</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Pos</TableCell>
                          <TableCell>Team</TableCell>
                          <TableCell align="center">W</TableCell>
                          <TableCell align="center">L</TableCell>
                          <TableCell align="center">GW</TableCell>
                          <TableCell align="center">GD</TableCell>
                          <TableCell align="center">Pts</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {standings.map((s, idx) => {
                          const isMine = !!myTeam && myTeam.id === s.teamId;
                          return (
                            <TableRow key={s.teamId} sx={isMine ? { bgcolor: "primary.50" } : undefined}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell sx={{ fontWeight: isMine ? 700 : 400 }}>{getTeamName(s.teamId)}</TableCell>
                              <TableCell align="center">{s.wins}</TableCell>
                              <TableCell align="center">{s.losses}</TableCell>
                              <TableCell align="center">{s.gamesWon}</TableCell>
                              <TableCell align="center">{s.gamesDiff}</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 700 }}>{s.points}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // ---- shared match card (used by Matches + My Team tabs) ----
  const renderMatchCard = (m: FlatMatch, highlightTeamId?: string) => {
    const team1Label = m.stage === "knockout" ? getKnockoutSideLabel(m, true) : getTeamName(m.team1Id);
    const team2Label = m.stage === "knockout" ? getKnockoutSideLabel(m, false) : getTeamName(m.team2Id);
    const isHighlighted = !!highlightTeamId && (m.team1Id === highlightTeamId || m.team2Id === highlightTeamId);
    return (
      <Card
        key={m.id}
        variant="outlined"
        sx={{
          borderRadius: 3,
          borderColor: isHighlighted ? "primary.main" : "divider",
          bgcolor: m.isScored ? "success.50" : "background.paper",
          boxShadow: isHighlighted ? "0 10px 30px rgba(25, 118, 210, 0.14)" : "none",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ py: 1.5, px: { xs: 1.5, sm: 2 }, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1, flexWrap: "wrap", gap: 0.75 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Chip
                label={stageLabel(m)}
                size="small"
                color={m.stage === "group" ? "primary" : "secondary"}
                variant={isHighlighted ? "filled" : "outlined"}
              />
              {isHighlighted && <Chip label="Your Match" size="small" color="success" />}
            </Stack>
            <Stack direction="row" spacing={0.5}>
              {m.startTime && <Chip label={formatTime(m.startTime)} size="small" variant="outlined" />}
              {m.courtNumber && <Chip label={`Court ${m.courtNumber}`} size="small" variant="outlined" />}
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={{ xs: 1.25, md: 1.5 }}
          >
            <Typography
              sx={{
                flex: 1,
                fontWeight: (m.winnerId && m.winnerId === m.team1Id) || m.team1Id === highlightTeamId ? 700 : 500,
                fontSize: { xs: "0.96rem", md: "1rem" },
              }}
            >
              {team1Label}
            </Typography>

            <Box
              sx={{
                alignSelf: { xs: "center", md: "stretch" },
                minWidth: 96,
                px: 1.5,
                py: 0.75,
                borderRadius: 999,
                textAlign: "center",
                bgcolor: isHighlighted ? "primary.main" : "action.hover",
                color: isHighlighted ? "primary.contrastText" : "text.primary",
              }}
            >
              <Typography fontWeight={800} sx={{ letterSpacing: 0.5 }}>
                {m.team1Games ?? "-"} : {m.team2Games ?? "-"}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.82 }}>
                {m.isScored ? "Result" : "Scheduled"}
              </Typography>
            </Box>

            <Typography
              sx={{
                flex: 1,
                textAlign: { xs: "left", md: "right" },
                fontWeight: (m.winnerId && m.winnerId === m.team2Id) || m.team2Id === highlightTeamId ? 700 : 500,
                fontSize: { xs: "0.96rem", md: "1rem" },
              }}
            >
              {team2Label}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // ---- Matches tab ----
  const renderMatchesTab = () => {
    const matches = getAllMatches().filter((m) => !m.isCancelled && !m.isByeCard);
    if (matches.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">No matches scheduled yet.</Typography>
        </Box>
      );
    }
    const slotMap = new Map<string, FlatMatch[]>();
    matches.forEach((m) => {
      const key = m.startTime ? new Date(m.startTime).toISOString() : "unscheduled";
      if (!slotMap.has(key)) slotMap.set(key, []);
      slotMap.get(key)!.push(m);
    });
    const slots = Array.from(slotMap.entries()).sort(([a], [b]) => {
      if (a === "unscheduled") return 1;
      if (b === "unscheduled") return -1;
      return a.localeCompare(b);
    });
    return (
      <Stack spacing={2.5}>
        {slots.map(([key, slotMatches]) => (
          <Box key={key}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {key === "unscheduled" ? "Unscheduled" : `${formatDate(key)} · ${formatTime(key)}`}
            </Typography>
            <Stack spacing={1}>
              {slotMatches
                .slice()
                .sort((a, b) => (a.courtNumber || 99) - (b.courtNumber || 99))
                .map((m) => renderMatchCard(m))}
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  };

  // ---- Bracket tab ----
  const renderBracketColumn = (matches: FlatMatch[], round: string) => (
    <Stack key={round} spacing={1.5} sx={{ minWidth: 220 }}>
      <Typography variant="subtitle2" fontWeight={700} textAlign="center">{formatRoundLabel(round)}</Typography>
      {matches.map((m) => {
        if (m.isByeCard) {
          return (
            <Card key={m.id} variant="outlined" sx={{ borderStyle: "dashed" }}>
              <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Round 1
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 0.5, borderRadius: 1 }}>
                  <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: 700 }}>
                    {getTeamName(m.byeTeamId)}
                  </Typography>
                </Box>
                <Chip label="BYE" size="small" sx={{ mt: 0.5, width: "100%" }} />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Advances directly — no Round 1 opponent
                </Typography>
              </CardContent>
            </Card>
          );
        }
        return (
        <Card key={m.id} variant="outlined" sx={{ bgcolor: m.winnerId ? "success.50" : "background.paper" }}>
          <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Match {getMatchLabel(m.matchNumber)}{m.courtNumber ? ` · Ct ${m.courtNumber}` : ""}{m.startTime ? ` · ${formatTime(m.startTime)}` : ""}
            </Typography>
            {[true, false].map((isTeam1) => {
              const teamId = isTeam1 ? m.team1Id : m.team2Id;
              const games = isTeam1 ? m.team1Games : m.team2Games;
              const isWinner = !!teamId && teamId === m.winnerId;
              return (
                <Box
                  key={String(isTeam1)}
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 0.5, borderRadius: 1, bgcolor: isWinner ? "success.100" : "transparent", mt: isTeam1 ? 0 : 0.5 }}
                >
                  <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: isWinner ? 700 : 400 }}>
                    {getKnockoutSideLabel(m, isTeam1)}
                  </Typography>
                  {games !== undefined && games !== null && <Typography fontWeight={700} sx={{ ml: 0.5 }}>{games}</Typography>}
                </Box>
              );
            })}
            {m.isWalkover && (
              <Chip label="Walkover (BYE)" size="small" color="success" sx={{ mt: 0.5, width: "100%" }} />
            )}
          </CardContent>
        </Card>
        );
      })}
    </Stack>
  );

  const renderBracketSection = (allMatches: FlatMatch[], label: string) => {
    const sectionMatches = allMatches.filter((m) => m.bracketLabel === label);
    if (sectionMatches.length === 0) return null;
    const mainMatches = sectionMatches.filter((m) => !isConsolationRound(m.round));
    const consolationMatches = sectionMatches.filter((m) => isConsolationRound(m.round));
    const mainRounds = sortRounds(Array.from(new Set(mainMatches.map((m) => m.round!))), false);
    const consolationRounds = sortRounds(Array.from(new Set(consolationMatches.map((m) => m.round!))), true);
    return (
      <Box key={label} sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>{label}</Typography>
        <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1 }}>
          {mainRounds.map((round) => renderBracketColumn(mainMatches.filter((m) => m.round === round), round))}
        </Box>
        {consolationRounds.length > 0 && (
          <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1, mt: 2 }}>
            {consolationRounds.map((round) => renderBracketColumn(consolationMatches.filter((m) => m.round === round), round))}
          </Box>
        )}
      </Box>
    );
  };

  const renderBracketTab = () => {
    const matches = getAllMatches().filter((m) => m.stage === "knockout");
    if (matches.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">The knockout bracket hasn't been generated yet.</Typography>
        </Box>
      );
    }
    return (
      <Box>
        {renderBracketSection(matches, bracketNames.primary)}
        {renderBracketSection(matches, bracketNames.secondary)}
      </Box>
    );
  };

  // ---- My Team tab ----
  const renderMyTeamTab = () => {
    if (!playerUser) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>Log in with your player account to see your team's matches.</Typography>
          <Button variant="contained" onClick={() => navigate("/player/login")}>Log in</Button>
        </Box>
      );
    }
    if (!myTeam) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">You're not registered for this tournament.</Typography>
        </Box>
      );
    }
    const myMatches = getAllMatches().filter((m) => (m.team1Id === myTeam.id || m.team2Id === myTeam.id) && !m.isCancelled);
    const upcoming = myMatches
      .filter((m) => !m.isScored)
      .sort((a, b) => {
        if (!a.startTime) return 1;
        if (!b.startTime) return -1;
        return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      });
    const previous = myMatches
      .filter((m) => m.isScored)
      .sort((a, b) => {
        if (!a.startTime) return 1;
        if (!b.startTime) return -1;
        return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
      });
    const partnerName = playerUser.playerId === myTeam.player1Id ? myTeam.player2Name : myTeam.player1Name;
    const nextMatch = upcoming[0] || null;
    return (
      <Stack spacing={3}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1.4fr) minmax(0, 1fr)" },
            gap: 2,
            width: "100%",
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 2.25,
              borderRadius: 3,
              width: "100%",
              background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.16)} 0%, ${alpha(t.palette.primary.main, 0.04)} 100%)`,
              borderColor: "primary.main",
            }}
          >
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
              Your Team
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mt: 0.25, mb: 0.5, fontSize: { xs: "1.7rem", md: "2rem" } }}>
              {getTeamName(myTeam.id)}
            </Typography>
            {partnerName && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Playing alongside <strong>{partnerName}</strong>
              </Typography>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`${upcoming.length} upcoming`} color="primary" variant="filled" />
              <Chip label={`${previous.length} completed`} variant="outlined" />
              <Chip label={nextMatch ? `Next: ${formatTime(nextMatch.startTime) || "TBD"}` : "Next: TBD"} variant="outlined" />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, width: "100%", height: "100%" }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1.1 }}>
              Match Pulse
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 1.25,
                mt: 0.25,
              }}
            >
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: "action.hover" }}>
                <Typography variant="caption" color="text.secondary">Scheduled</Typography>
                <Typography variant="h5" fontWeight={800}>{myMatches.length}</Typography>
              </Box>
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: "action.hover" }}>
                <Typography variant="caption" color="text.secondary">Upcoming</Typography>
                <Typography variant="h5" fontWeight={800}>{upcoming.length}</Typography>
              </Box>
              <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: "action.hover" }}>
                <Typography variant="caption" color="text.secondary">Results</Typography>
                <Typography variant="h5" fontWeight={800}>{previous.length}</Typography>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {nextMatch
                ? `Your next court appearance is ${formatTime(nextMatch.startTime) || "to be announced"}${nextMatch.courtNumber ? ` on Court ${nextMatch.courtNumber}` : ""}.`
                : "Your full team schedule will appear here as soon as matches are published."}
            </Typography>
          </Paper>
        </Box>

        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={800}>Upcoming Matches</Typography>
              <Typography variant="body2" color="text.secondary">Stay ready for your next fixtures.</Typography>
            </Box>
            <Chip label={`${upcoming.length} scheduled`} color="primary" variant="outlined" />
          </Stack>
          {upcoming.length === 0 ? (
            <Typography color="text.secondary" variant="body2">No upcoming matches scheduled.</Typography>
          ) : (
            <Stack spacing={1.25}>{upcoming.map((m) => renderMatchCard(m, myTeam.id))}</Stack>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5, flexWrap: "wrap", gap: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={800}>Previous Results</Typography>
              <Typography variant="body2" color="text.secondary">Completed matches and final scores.</Typography>
            </Box>
            <Chip label={`${previous.length} logged`} color="success" variant="outlined" />
          </Stack>
          {previous.length === 0 ? (
            <Typography color="text.secondary" variant="body2">No results yet.</Typography>
          ) : (
            <Stack spacing={1.25}>{previous.map((m) => renderMatchCard(m, myTeam.id))}</Stack>
          )}
        </Paper>
      </Stack>
    );
  };

  // ---- QR Code tab ----
  const renderQrCodeTab = () => {
    if (!playerUser) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>Log in with your player account to access your gift pickup QR.</Typography>
          <Button variant="contained" onClick={() => navigate("/player/login")}>Log in</Button>
        </Box>
      );
    }

    if (!myTeam) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">You're not registered for this tournament.</Typography>
        </Box>
      );
    }

    if (myGiftLoading) {
      return (
        <Paper variant="outlined" sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading gift pickup QR...</Typography>
        </Paper>
      );
    }

    if (!myGift?.giftsEnabled) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography color="text.secondary">Gift pickup is not enabled for this tournament.</Typography>
        </Box>
      );
    }

    const availableGiftOptions = Array.isArray(myGift.giftOptions)
      ? myGift.giftOptions.filter((gift) => typeof gift === "string" && gift.trim().length > 0)
      : [];
    const hasMultipleGiftOptions = availableGiftOptions.length > 1;
    const qrDescription = hasMultipleGiftOptions
      ? "Show this QR to the organizer. You are eligible for one of the sponsor gifts listed below."
      : (myGift.giftDescription || "Show this QR to the organizer to collect your gift.");

    return (
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          background: (t) => `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.10)} 0%, ${alpha(t.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Typography variant="overline" color="primary.main" sx={{ fontWeight: 700, letterSpacing: 1.1 }}>Gift Pickup</Typography>
        <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>Gift Pickup QR</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {qrDescription}
        </Typography>

        {availableGiftOptions.length > 0 && (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
            {availableGiftOptions.map((gift) => (
              <Chip
                key={gift}
                label={gift}
                variant="outlined"
              />
            ))}
          </Stack>
        )}

        <Box
          sx={{
            display: "inline-flex",
            p: 1,
            borderRadius: 1,
            bgcolor: "common.white",
            border: "1px solid",
            borderColor: "divider",
            opacity: myGift.redeemed ? 0.45 : 1,
          }}
        >
          <QRCodeSVG value={myGift.qrPayload || ""} size={200} />
        </Box>

        {myGift.redeemed ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            Picked up{formatGiftDateTime(myGift.redeemedAt) ? ` at ${formatGiftDateTime(myGift.redeemedAt)}` : ""}.
          </Alert>
        ) : (
          <Typography variant="body2" sx={{ mt: 2 }}>
            Show this to an organizer at the gift table to collect it.
          </Typography>
        )}
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tournament) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">Tournament not found.</Typography>
        <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate("/")}>Back to Home</Button>
      </Box>
    );
  }

  const statusLabel: Record<string, string> = {
    upcoming: "Coming Soon",
    registration: "Registration Open",
    "group-stage": "Group Stage",
    knockout: "Knockout Stage",
    completed: "Completed",
    cancelled: "Cancelled",
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate("/")} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>{tournament.name}</Typography>
          <Chip label={statusLabel[tournament.status] || tournament.status} size="small" sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff" }} />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: { xs: 2, md: 2.5 },
            borderRadius: 4,
            color: "common.white",
            // Some themes (Padel Eagle) paint every heading in a dark brand colour;
            // on this dark hero they must stay white.
            "& h1, & h2, & h3, & h4, & h5, & h6": { color: "inherit" },
            // Built from the organisation's own colours so the hero matches the app bar and chips.
            backgroundImage: (t) => {
              const base = t.palette.primary.dark;
              const deep = darken(base, 0.45);
              return (tournament as any).posterImageUrl
                ? `linear-gradient(135deg, ${alpha(deep, 0.9)} 0%, ${alpha(base, 0.72)} 48%, ${alpha(deep, 0.92)} 100%), url(${(tournament as any).posterImageUrl})`
                : `linear-gradient(135deg, ${base} 0%, ${darken(base, 0.25)} 55%, ${deep} 100%)`;
            },
            backgroundSize: "cover",
            backgroundPosition: "center",
            boxShadow: "0 24px 60px rgba(0,0,0,0.24)",
          }}
        >
          <Grid container spacing={2.5} alignItems="stretch">
            <Grid item xs={12} md={7}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", mb: 1.25 }}>
                <Chip label={statusLabel[tournament.status] || tournament.status} size="small" sx={{ bgcolor: "rgba(255,255,255,0.16)", color: "#fff", fontWeight: 700 }} />
                <Chip label={tournament.level} size="small" sx={{ bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontWeight: 700 }} />
              </Stack>
              <Typography variant="h3" sx={{ fontWeight: 900, fontSize: { xs: "1.9rem", md: "2.6rem" }, lineHeight: 1.05, mb: 1 }}>
                {tournament.name}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.5, sm: 1.5 }} sx={{ color: "rgba(255,255,255,0.82)" }}>
                {tournament.startDate && (
                  <Typography variant="body2">
                    {new Date((tournament.startDate as any).seconds ? (tournament.startDate as any).seconds * 1000 : tournament.startDate).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                  </Typography>
                )}
                {tournament.venue && <Typography variant="body2">{tournament.venue}</Typography>}
              </Stack>

              <Grid container spacing={1.25} sx={{ mt: 1.25 }}>
                <Grid item xs={4}>
                  <Box sx={{ p: 1.25, borderRadius: 3, bgcolor: "rgba(255,255,255,0.10)", textAlign: "center", backdropFilter: "blur(10px)" }}>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.72)" }}>Teams</Typography>
                    <Typography variant="h6" fontWeight={800}>{tournament.registeredTeams?.length || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ p: 1.25, borderRadius: 3, bgcolor: "rgba(255,255,255,0.10)", textAlign: "center", backdropFilter: "blur(10px)" }}>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.72)" }}>Groups</Typography>
                    <Typography variant="h6" fontWeight={800}>{tournament.groups?.length || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ p: 1.25, borderRadius: 3, bgcolor: "rgba(255,255,255,0.10)", textAlign: "center", backdropFilter: "blur(10px)" }}>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.72)" }}>Status</Typography>
                    <Typography variant="h6" fontWeight={800}>{tournament.status === "registration" ? "Open" : tournament.status === "completed" ? "Done" : "Live"}</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={5}>
              {(tournament as any).posterImageUrl ? (
                <Box
                  sx={{
                    height: "100%",
                    minHeight: { xs: 240, md: 320 },
                    p: 1,
                    borderRadius: 4,
                    bgcolor: "rgba(255,255,255,0.08)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={(tournament as any).posterImageUrl}
                    alt={`${tournament.name} poster`}
                    sx={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                      maxHeight: { xs: 360, md: 420 },
                      objectFit: "contain",
                      objectPosition: "center",
                      borderRadius: 3,
                      boxShadow: "0 16px 32px rgba(0,0,0,0.28)",
                      bgcolor: "rgba(0,0,0,0.18)",
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ p: 2, borderRadius: 4, bgcolor: "rgba(255,255,255,0.08)", height: "100%", display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.76)" }}>
                    Tournament updates, match schedule, standings, and your team information are all available below.
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={1} sx={{ borderRadius: 4, overflow: "hidden" }}>
          {isMobile ? (
            <Box
              sx={{
                px: 1.5,
                pt: 1.5,
                pb: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                background: (t) => `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.10)} 0%, ${alpha(t.palette.primary.main, 0.02)} 100%)`,
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="tournament-info-tab-select-label" sx={{ fontWeight: 700 }}>Section</InputLabel>
                <Select
                  labelId="tournament-info-tab-select-label"
                  value={tab}
                  label="Section"
                  onChange={(e) => setTab(Number(e.target.value))}
                  sx={{
                    borderRadius: 3,
                    bgcolor: "rgba(255,255,255,0.04)",
                    "& .MuiSelect-select": {
                      py: 1.15,
                      fontWeight: 700,
                    },
                  }}
                >
                  <MenuItem value={0}>My Team</MenuItem>
                  <MenuItem value={1}>QR Code</MenuItem>
                  <MenuItem value={2}>Standings</MenuItem>
                  <MenuItem value={3}>Matches</MenuItem>
                  <MenuItem value={4}>Bracket</MenuItem>
                </Select>
              </FormControl>
            </Box>
          ) : (
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: { xs: 1, md: 1.5 },
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: 999,
                },
                "& .MuiTab-root": {
                  minHeight: 58,
                  textTransform: "none",
                  fontWeight: 700,
                  letterSpacing: 0,
                },
              }}
            >
              <Tab label="My Team" />
              <Tab label="QR Code" />
              <Tab label="Standings" />
              <Tab label="Matches" />
              <Tab label="Bracket" />
            </Tabs>
          )}
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <TabPanel value={tab} index={0}>{renderMyTeamTab()}</TabPanel>
            <TabPanel value={tab} index={1}>{renderQrCodeTab()}</TabPanel>
            <TabPanel value={tab} index={2}>{renderStandingsTab()}</TabPanel>
            <TabPanel value={tab} index={3}>{renderMatchesTab()}</TabPanel>
            <TabPanel value={tab} index={4}>{renderBracketTab()}</TabPanel>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
